import { useEffect, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router'
import { addDoc, collection, onSnapshot, orderBy, query, serverTimestamp } from 'firebase/firestore'
import { db } from '~/firebase.client'

export function SectionTitle({ eyebrow, title, description }) {
  return (
    <div className="section-title">
      <span className="eyebrow">{eyebrow}</span>
      <h2>{title}</h2>
      {description ? <p>{description}</p> : null}
    </div>
  )
}

/* ── Inline SVG icons ── */
function IconGear() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  )
}

function IconZap() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  )
}

function IconWifi() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12.55a11 11 0 0 1 14.08 0" />
      <path d="M1.42 9a16 16 0 0 1 21.16 0" />
      <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
      <circle cx="12" cy="20" r="1" fill="currentColor" stroke="none" />
    </svg>
  )
}

function IconClipboard() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <rect x="8" y="2" width="8" height="4" rx="1" />
      <line x1="9" y1="12" x2="15" y2="12" />
      <line x1="9" y1="16" x2="13" y2="16" />
    </svg>
  )
}

function IconCheck() {
  return (
    <svg className="check-icon" width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="10" r="10" fill="rgba(220,47,39,0.12)" />
      <path d="M5.5 10.5L8.5 13.5L14.5 7.5" stroke="var(--color-primary)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function StarRating() {
  return (
    <div className="testimonial-stars" role="img" aria-label="5 out of 5 stars">
      {[0, 1, 2, 3, 4].map((i) => (
        <svg key={i} width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      ))}
    </div>
  )
}

const SERVICE_ICONS = [<IconGear />, <IconZap />, <IconWifi />, <IconClipboard />]
const SERVICE_ACCENTS = [
  'service-accent--red',
  'service-accent--purple',
  'service-accent--navy',
  'service-accent--gold',
]

/* ── TopBar ── */
export function TopBar() {
  const [menuOpen, setMenuOpen] = useState(false)
  const location = useLocation()
  const isServicesActive = location.pathname === '/' && location.hash === '#services'

  return (
    <header className="top-bar">
      <div className="container top-bar-inner">
        <Link
          className="brand"
          to="/"
          aria-label="S.J.S Technology — home"
          onClick={() => setMenuOpen(false)}
        >
          <span className="brand-logo-row">
            <img className="brand-logo-icon" src="/s_logo_transparent.webp" alt="" width={54} height={54} />
            <img className="brand-logo-text" src="/sjs_text_transparent.webp" alt="S.J.S Technology" width={171} height={18} />
          </span>
        </Link>

        <nav className={`nav-menu ${menuOpen ? 'nav-menu--open' : ''}`} id="site-nav">
          <NavLink to="/" end className={({ isActive }) => isActive && !isServicesActive ? 'active' : ''} onClick={() => setMenuOpen(false)}>Home</NavLink>
          <NavLink to="/products" onClick={() => setMenuOpen(false)}>Products</NavLink>
          <Link to="/#services" className={isServicesActive ? 'active' : ''} onClick={() => setMenuOpen(false)}>Services</Link>
          <NavLink to="/about" onClick={() => setMenuOpen(false)}>About</NavLink>
          <NavLink to="/reviews" onClick={() => setMenuOpen(false)}>Reviews</NavLink>
          <NavLink to="/events" onClick={() => setMenuOpen(false)}>Events</NavLink>
          <NavLink to="/contact" onClick={() => setMenuOpen(false)}>Contact</NavLink>
        </nav>

        <div className="top-bar-actions">
          <Link
            className="btn btn-small top-bar-cta"
            to="/contact"
            onClick={() => setMenuOpen(false)}
          >
            Get a Quote
          </Link>
          <button
            type="button"
            className={`nav-toggle ${menuOpen ? 'nav-toggle--open' : ''}`}
            aria-expanded={menuOpen}
            aria-controls="site-nav"
            onClick={() => setMenuOpen((o) => !o)}
          >
            <span className="nav-toggle-lines" aria-hidden="true" />
            <span className="visually-hidden">{menuOpen ? 'Close menu' : 'Open menu'}</span>
          </button>
        </div>
      </div>
    </header>
  )
}

/* ── Hero ── */
export function HeroSection() {
  return (
    <section className="hero-section">
      <div className="hero-bg" aria-hidden="true" />
      <div className="container hero-grid">
        <div className="hero-copy-block">
          <p className="eyebrow">Home of VICTORY Machinery &amp; VisionJet Inks</p>
          <h1>
            <span className="hero-line">Next-Gen Printing</span>{' '}
            <span className="hero-line hero-line--muted">for your business</span>
          </h1>
          <p className="hero-lead">
            Welcome to S.J.S. Technology — your Bengaluru-based partner for
            high-performance digital printing machines, premium inks, and
            dependable after-sales support.
          </p>
          <div className="hero-actions">
            <Link className="btn" to="/contact">Get a Quote</Link>
            <Link className="btn btn-ghost" to="/products">Explore Machines</Link>
          </div>
          <div className="hero-credibility" aria-label="Key achievements">
            <div className="hero-cred-item">
              <strong>50+</strong>
              <span>Machines installed</span>
            </div>
            <div className="hero-cred-divider" aria-hidden="true" />
            <div className="hero-cred-item">
              <strong>100+</strong>
              <span>Happy clients</span>
            </div>
            <div className="hero-cred-divider" aria-hidden="true" />
            <div className="hero-cred-item">
              <strong>24/7</strong>
              <span>Service support</span>
            </div>
          </div>
        </div>

        <div className="hero-visual">
          <div className="hero-image-wrap">
            <img
              className="hero-image"
              src="/victory.webp"
              alt="VICTORY Digital Printing Machines"
              width={400}
              height={400}
              fetchPriority="high"
            />
            <div className="hero-image-badge">
              <strong>Bengaluru</strong>
              <span>Showroom &amp; Service</span>
            </div>
          </div>
          <div className="hero-features">
            {[
              'VICTORY brand — heavy-duty machines',
              'Authorized VisionJet ink dealer',
              'Live demo at showroom',
              'Installation, training & AMC',
            ].map((item) => (
              <div className="hero-feature-chip" key={item}>
                <IconCheck />
                {item}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

/* ── Services ── */
export function ServicesSection({ items }) {
  return (
    <section id="services" className="section-pad">
      <div className="container">
        <SectionTitle
          eyebrow="Our Services"
          title="End-to-end support for your business"
          description="Machines are just the start — we provide inks, installation, training, and ongoing maintenance."
        />
        <div className="card-grid cols-4">
          {items.map((service, i) => (
            <article
              className={`info-card ${SERVICE_ACCENTS[i % SERVICE_ACCENTS.length]}`}
              key={service.title}
            >
              <div className="service-icon">
                {SERVICE_ICONS[i % SERVICE_ICONS.length]}
              </div>
              <h3>{service.title}</h3>
              <p>{service.description}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ── Industries ── */
export function IndustriesSection({ items, preview = false }) {
  const displayItems = preview ? items.slice(0, 4) : items
  return (
    <section id="industries" className="section-pad muted">
      <div className="container">
        <SectionTitle
          eyebrow="Industries We Serve"
          title="Powering print across sectors"
          description="Our machines and inks serve diverse applications — from signage shops to textile factories."
        />
        <div className="chip-wrap">
          {displayItems.map((industry) => (
            <span className="chip" key={industry}>{industry}</span>
          ))}
        </div>
        {preview && (
          <div className="section-view-all">
            <Link className="btn btn-ghost-light" to="/industries">View all industries →</Link>
          </div>
        )}
      </div>
    </section>
  )
}

/* ── Process ── */
export function ProcessSection({ items, preview = false }) {
  return (
    <section id="process" className="section-pad">
      <div className="container">
        <SectionTitle
          eyebrow="How It Works"
          title="From enquiry to production in 4 steps"
          description="A simple, transparent process to get you up and running with the right machine."
        />
        <div className="process-track card-grid cols-4">
          {items.map((item) => (
            <article className="process-card" key={item.step}>
              <div className="process-step-badge" aria-hidden="true">{item.step}</div>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </article>
          ))}
        </div>
        {preview && (
          <div className="section-view-all">
            <Link className="btn btn-ghost-light" to="/process">Explore our full process →</Link>
          </div>
        )}
      </div>
    </section>
  )
}

/* ── Achievements ── */
export function AchievementsSection({ items }) {
  return (
    <section className="section-pad dark">
      <div className="container">
        <div className="stats-grid">
          {items.map((item) => (
            <div key={item.label}>
              <h3>{item.metric}</h3>
              <p>{item.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ── Reviews (Firestore-powered) ── */
export function ReviewsSection({ preview = false }) {
  const [reviews, setReviews] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [form, setForm] = useState({ name: '', company: '', rating: 5, review: '' })
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    const q = query(collection(db, 'reviews'), orderBy('createdAt', 'desc'))
    const unsub = onSnapshot(
      q,
      (snap) => {
        setReviews(
          snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((r) => r.approved),
        )
        setLoading(false)
      },
      () => setLoading(false),
    )
    return unsub
  }, [])

  async function handleSubmit(e) {
    e.preventDefault()
    if (!form.name.trim() || !form.review.trim()) return
    setSubmitting(true)
    try {
      await addDoc(collection(db, 'reviews'), {
        name: form.name.trim(),
        company: form.company.trim(),
        rating: Number(form.rating),
        review: form.review.trim(),
        approved: false,
        source: 'customer',
        createdAt: serverTimestamp(),
      })
      setSubmitted(true)
      setForm({ name: '', company: '', rating: 5, review: '' })
    } catch (err) {
      console.error(err)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section className="section-pad muted">
      <div className="container">
        <div className="reviews-header">
          <SectionTitle
            eyebrow="Client Reviews"
            title="What our customers say"
          />
          {!formOpen && !submitted && (
            <button className="btn btn-ghost-light" onClick={() => setFormOpen(true)}>
              Write a Review
            </button>
          )}
        </div>

        {/* Review submission form */}
        {formOpen && !submitted && (
          <div className="review-form-card">
            <form className="review-form" onSubmit={handleSubmit}>
              <h3 className="review-form-title">Share your experience</h3>
              <div className="enquiry-row">
                <input
                  placeholder="Your Name *"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  required
                />
                <input
                  placeholder="Company (optional)"
                  value={form.company}
                  onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
                />
              </div>
              <select
                value={form.rating}
                onChange={(e) => setForm((f) => ({ ...f, rating: Number(e.target.value) }))}
              >
                <option value={5}>5 Stars — Excellent</option>
                <option value={4}>4 Stars — Very Good</option>
                <option value={3}>3 Stars — Good</option>
                <option value={2}>2 Stars — Fair</option>
                <option value={1}>1 Star — Poor</option>
              </select>
              <textarea
                placeholder="Write your review… *"
                value={form.review}
                onChange={(e) => setForm((f) => ({ ...f, review: e.target.value }))}
                rows={3}
                required
              />
              <div className="review-form-actions">
                <button className="btn" type="submit" disabled={submitting}>
                  {submitting ? 'Submitting…' : 'Submit Review'}
                </button>
                <button className="btn btn-ghost-light" type="button" onClick={() => setFormOpen(false)}>
                  Cancel
                </button>
              </div>
              <p className="review-form-note">Your review will be visible after admin approval.</p>
            </form>
          </div>
        )}

        {submitted && (
          <div className="review-submitted">
            <p>Thank you for your review! It will be visible once approved.</p>
          </div>
        )}

        {/* Approved reviews */}
        {loading ? (
          <div className="products-loading">
            <div className="products-loading-spinner" aria-hidden="true" />
            <p>Loading reviews…</p>
          </div>
        ) : reviews.length === 0 && !submitted ? (
          <p style={{ color: 'var(--color-muted)', fontSize: 15 }}>No reviews yet. Be the first to share your experience!</p>
        ) : (
          <div className="card-grid cols-2">
            {(preview ? reviews.slice(0, 4) : reviews).map((rev) => (
              <blockquote className="quote-card" key={rev.id}>
                <div className="testimonial-stars" role="img" aria-label={`${rev.rating} out of 5 stars`}>
                  {[0, 1, 2, 3, 4].map((i) => (
                    <svg key={i} width="14" height="14" viewBox="0 0 24 24" fill={i < rev.rating ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                  ))}
                </div>
                <span className="quote-mark" aria-hidden="true">"</span>
                <p>{rev.review}</p>
                <cite>{rev.name}{rev.company ? `, ${rev.company}` : ''}</cite>
              </blockquote>
            ))}
          </div>
        )}

        {preview && reviews.length > 4 && (
          <div className="section-view-all">
            <Link className="btn btn-ghost-light" to="/reviews">See all reviews →</Link>
          </div>
        )}
      </div>
    </section>
  )
}

/* ── Client logos ── */
const CLIENT_NAMES = [
  'Easy Prints and Signs, Bengaluru',
  'Aadwell Digital Arts, Vellore',
  'Digital Park, Katpadi',
  'Gallary Production, Sirsi',
  'Sri Sastha Digital, Vellore',
  'G.S.Creation, Bengaluru',
  'Sai Digital World, Vellore',
]

export function ClientsStrip() {
  return (
    <section className="clients-section">
      <div className="container">
        <p className="clients-label">Trusted by leading industrial companies</p>
        <div className="clients-logos">
          {CLIENT_NAMES.map((name) => (
            <span key={name}>{name}</span>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ── Enquiry Form ── */
export function EnquirySection() {
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')

  async function handleSubmit(e) {
    e.preventDefault()
    const form = e.target
    setSubmitting(true)
    setError('')
    try {
      const { submitEnquiry } = await import('../enquiryService')
      await submitEnquiry({
        name: form.elements['name'].value,
        company: form.elements['company'].value,
        email: form.elements['email'].value,
        phone: form.elements['phone'].value,
        service: form.elements['service'].value,
        message: form.elements['message'].value,
      })
      setSubmitted(true)
    } catch (err) {
      console.error(err)
      setError('Something went wrong. Please try again or call us directly.')
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <section id="enquiry" className="section-pad enquiry-section">
        <div className="container">
          <div className="review-submitted" style={{ textAlign: 'center', padding: '32px 24px' }}>
            <p style={{ margin: 0, fontSize: '1.1rem' }}>Thank you for your enquiry! We will get back to you within 24 hours.</p>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section id="enquiry" className="section-pad enquiry-section">
      <div className="container enquiry-grid">
        <div className="enquiry-copy">
          <span className="eyebrow">Quick Enquiry</span>
          <h2>Tell us what you need</h2>
          <p>
            Share a few details and our team will get back within 24 hours with
            a personalised recommendation and quote.
          </p>
        </div>
        <div className="enquiry-form-card">
          {error && <div className="admin-alert admin-alert-error" style={{ marginBottom: 16 }}>{error}</div>}
          <form className="enquiry-form" onSubmit={handleSubmit}>
            <div className="enquiry-row">
              <input name="name" placeholder="Full Name *" required />
              <input name="company" placeholder="Company Name" />
            </div>
            <div className="enquiry-row">
              <input name="email" type="email" placeholder="Email *" required />
              <input name="phone" type="tel" placeholder="Phone Number" />
            </div>
            <select name="service" defaultValue="" aria-label="Service type">
              <option value="" disabled>What are you looking for?</option>
              <option>Digital Printing Machine</option>
              <option>VisionJet Inks & Consumables</option>
              <option>Spare Parts</option>
              <option>Installation & Training</option>
              <option>Annual Maintenance Contract</option>
              <option>Other</option>
            </select>
            <textarea name="message" placeholder="Describe your requirement…" rows={3} />
            <button className="btn" type="submit" disabled={submitting}>
              {submitting ? 'Sending…' : 'Send Enquiry'}
            </button>
          </form>
        </div>
      </div>
    </section>
  )
}

/* ── Contact CTA ── */
export function ContactCtaSection() {
  return (
    <section id="contact" className="section-pad cta">
      <div className="container cta-box cta-box-split">
        <div>
          <span className="eyebrow">Ready to upgrade your production?</span>
          <h2>Get a VICTORY machine for your business</h2>
          <p>
            Tell us your printing requirements and we will recommend the right
            machine, arrange a live demo, and deliver a competitive quote.
          </p>
        </div>
        <div className="cta-actions">
          <Link className="btn" to="/contact">Get a Quote</Link>
          <Link className="btn btn-ghost" to="/products">View Products</Link>
        </div>
      </div>
    </section>
  )
}

/* ── Footer ── */
export function Footer() {
  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-grid">
          <div className="footer-brand-column">
            <span className="footer-logo-column">
              <img className="footer-logo-icon" src="/s_logo_transparent.webp" alt="" width={36} height={36} />
              <img className="footer-logo-text" src="/sjs_text_transparent.webp" alt="S.J.S Technology" width={209} height={22} />
            </span>
            <p className="footer-tagline">VICTORY printing machines, VisionJet inks &amp; complete service support. Bengaluru, India.</p>
            <a className="footer-link" href="https://www.sjstechnology.in/">
              www.sjstechnology.in
            </a>
            <div className="footer-social" aria-label="Follow us">
              <a
                href="https://whatsapp.com/channel/0029VaIZG4X17EmxSFC6zi24"
                className="footer-social-link"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp Channel"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </a>
              <a
                href="https://www.youtube.com/channel/UCZhmsUARpfRhhEkWAa1akdg"
                className="footer-social-link"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </a>
              <a
                href="https://www.instagram.com/sjs_technology?igsh=cDZramFnNm5hZHZt"
                className="footer-social-link"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                </svg>
              </a>
              <a
                href="https://www.facebook.com/sjstechnologyblr"
                className="footer-social-link"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
            </div>
          </div>

          <div className="footer-col">
            <p className="footer-col-title">Products</p>
            <ul className="footer-col-links">
              <li><Link to="/products">VICTORY Machines</Link></li>
              <li><Link to="/products">VisionJet Inks</Link></li>
              <li><Link to="/#services">Installation & AMC</Link></li>
              <li><Link to="/reviews">Reviews</Link></li>
            </ul>
          </div>

          <div className="footer-col">
            <p className="footer-col-title">Company</p>
            <ul className="footer-col-links">
              <li><Link to="/about">About Us</Link></li>
              <li><Link to="/events">Events</Link></li>
              <li><Link to="/contact">Contact Us</Link></li>
            </ul>
          </div>

          <div className="footer-col">
            <p className="footer-col-title">Contact</p>
            <ul className="footer-col-links">
              <li><a href="tel:+918884990111">+91 88849 90111</a></li>
              <li><a href="mailto:contact@sjstechnology.in">contact@sjstechnology.in</a></li>
              <li><a href="https://www.sjstechnology.in/">sjstechnology.in</a></li>
            </ul>
          </div>
        </div>

        <div className="footer-bottom">
          <p>© {new Date().getFullYear()} S.J.S Technology. All rights reserved.</p>
        </div>
      </div>
    </footer>
  )
}
